import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import classNames from "classnames";
import * as React from "react";

import { MessagePathNode } from "@/shared/domain/topics";
import { useDebouncedCallback } from "@/shared/hooks";
import {
  type PlotPanelState,
  actions,
  VizStateDispatchContext,
} from "@/shared/state/visualization";
import { LegendPosition } from "@/shared/state/visualization/schema";

import { OpenPanelSettings } from "../../controls";

import styles from "./Legend.module.css";
import { usePlotPanelContext } from "./panelContext";

interface LegendProps {
  state: PlotPanelState;
}

const positionClassMap: Record<LegendPosition, string> = {
  [LegendPosition.TopLeft]: styles.topLeft,
  [LegendPosition.TopRight]: styles.topRight,
  [LegendPosition.BottomLeft]: styles.bottomLeft,
  [LegendPosition.BottomRight]: styles.bottomRight,
};

export function Legend({ state }: LegendProps) {
  const vizStateDispatch = React.useContext(VizStateDispatchContext);
  const { isLegendShown } = usePlotPanelContext();

  const changeSeriesColor = useDebouncedCallback(function setSeriesColor(
    seriesId: string,
    color: string,
  ) {
    vizStateDispatch(
      actions.setSeriesConfig(state.id, seriesId, { lineColor: color }),
    );
  }, 15);

  const removeSeries = (seriesId: string) => {
    vizStateDispatch(actions.removeSeriesFromPlotPanel(state.id, seriesId));
  };

  const setSeriesVisibility = (seriesId: string, visible: boolean) => {
    vizStateDispatch(actions.setSeriesVisibility(state.id, seriesId, visible));
  };

  if (state.data.length === 0 || !isLegendShown) {
    return null;
  }

  const MAX_VISIBLE_SERIES = 7;

  const overflow =
    state.data.length > MAX_VISIBLE_SERIES ? (
      <li key="overflow" className={styles.legendItem}>
        ...and others
      </li>
    ) : null;
  const series = state.data.slice(0, MAX_VISIBLE_SERIES);

  const legendPosition = state.config?.legendPosition ?? LegendPosition.TopLeft;
  const positionClass =
    positionClassMap[legendPosition] ??
    positionClassMap[LegendPosition.TopLeft];

  return (
    <Box
      className={classNames(styles.container, positionClass)}
      component="ul"
      bgcolor={(theme) => alpha(theme.palette.paper.main, 0.8)}
    >
      {series.map((seriesItem) => {
        const lineColor = seriesItem.config?.lineColor;
        const formattedMessagePath = MessagePathNode.partsToString(
          seriesItem.data.messagePath.parts,
        );
        const defaultLabel = `${seriesItem.data.topic.name}.${formattedMessagePath}`;
        const displayLabel = seriesItem.config?.label ?? defaultLabel;
        return (
          <li className={styles.legendItem} key={seriesItem.id}>
            <div className={styles.legendItemLabel}>
              <input
                className={styles.legendItemColor}
                type="color"
                value={lineColor}
                name={seriesItem.id}
                onChange={(event) => {
                  changeSeriesColor(seriesItem.id, event.target.value);
                }}
              />
              <p
                role="button"
                tabIndex={0}
                aria-label="Toggle visibility of series"
                className={classNames("ph-no-capture", {
                  [styles.hidden]: !seriesItem.visible,
                })}
                onClick={function showOrHideSeries(event) {
                  // Prevent triggering the other chart onclick handlers
                  event.stopPropagation();

                  setSeriesVisibility(seriesItem.id, !seriesItem.visible);
                }}
              >
                {displayLabel}
              </p>
            </div>
            <div>
              <OpenPanelSettings className={styles.iconButton} state={state} />
              <IconButton
                className={styles.iconButton}
                onClick={function removeSeriesFromView() {
                  removeSeries(seriesItem.id);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </div>
          </li>
        );
      })}
      {overflow}
    </Box>
  );
}
